(ns minorodata.components.transformations.unit-test
  (:require
   ["lucide-react" :refer [Plus]]
   [clojure.string :as str]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.posthog-events :as posthog-events]
   [minorodata.app.utils :as app.utils]
   [minorodata.components.spinner.core :as c.spinner]
   [minorodata.sandbox.model :refer [->pipeline-transform-unsaved]]
   [mold-forms.core :as form-component]
   [mold-forms.model :refer [->form-model]]
   [re-frame.core :as re-frame]
   [re-posh.core :as re-posh]
   [react :as react]
   [ui.alert :as alert]
   [ui.button :refer [button]]
   [ui.dialog :refer [dialog-content dialog-description dialog-root
                      dialog-title]]
   [ui.fields :refer [input select]]
   [ui.typography :as typography]))


(defn check-procedures [{:keys [on-missing on-ready]}]
  (dispatch [:transformations.api/check-unit-tests {:on-failure on-missing
                                                    :on-success on-ready}]))


(defn create-procedures [_on-success]
  (let [on-success (fn [x]
                     (_on-success x)
                     (dispatch [:app.events/add-snack :success "Database procedures created"]))]
    (dispatch [:transformations.api/create-procedures-for-unit-tests
               {:on-success on-success}])))


(defn null-test
  [{:keys [sink-type catalog schema table column] :as props}]
  (case sink-type
    "snowflake" (str "; CALL " (or catalog "PROD") "." (or schema "KLEENE")  "._KLEENE_UT_NO_NULLS_IN_FIELD('" table "','" column "');")
    "redshift" (str "; CALL " (or catalog "kleene") "._kleene_ut_no_nulls_in_field('" table "','" column "');")
    "bigquery" (str "; CALL " (or catalog "kleene") "._kleene_ut_no_nulls_in_field('" table "','" column "');")))


(defn uniqueness-test
  [{:keys [sink-type catalog schema table column]}]
  (case sink-type
    "snowflake" (str "; CALL " (or catalog "PROD") "." (or schema "KLEENE")  "._KLEENE_UT_UNIQUENESS('" table "','" column "');")
    "redshift" (str "; CALL " (or catalog "kleene") "._kleene_ut_uniqueness('" table "','" column "');")
    "bigquery" (str "; CALL " (or catalog "kleene") "._kleene_ut_uniqueness('" table "','" column "');")))


(defn freshness-test
  [{:keys [sink-type catalog schema table column period period-value]}]
  (case sink-type
    "snowflake" (str "; CALL " (or catalog "PROD") "." (or schema "KLEENE")  "._KLEENE_UT_FRESHNESS('" table "', '" period "', '" period-value "', '" column "');")
    "redshift" (str "; CALL " (or catalog "kleene") "._kleene_ut_freshness('" table "', '" period "', '" period-value "', '" column "');")
    "bigquery" (str "; CALL " (or catalog "kleene") "._kleene_ut_freshness('" table "', '" period "', '" period-value "', '" column "');")))


(def test-fn-map
  {:null       null-test
   :freshness  freshness-test
   :uniqueness uniqueness-test})


(def periods
  [{:value "minutes"} {:value "hours"} {:value "days"} {:value "week"} {:value "months"}])


(def bq-periods
  [{:value "SECOND"} {:value "MINUTE"} {:value "HOUR"} {:value "DAY"}])


(def datetime-types
  #{"DATE" "DATETIME" "TIMESTAMP" "TIMESTAMPNTZ" "TIMESTAMPLTZ" "TIMESTAMPTZ" "TIMESTAMP_LTZ" "TIMESTAMP_NTZ" "TIMESTAMP_TZ"})


(defn extract-column-type [col]
  (let [raw-type (or (:type-name col)
                     (:type col)
                     (:data_type col))]
    (if (string? raw-type)
      (or (some->> raw-type
                   (re-find #"\"type\":\"([^\"]+)\"")
                   second)
          raw-type)
      raw-type)))

(defn date-column?
  [col]
  (let [column-type (extract-column-type col)]
    (when (string? column-type)
      (datetime-types (.toUpperCase column-type)))))


(defn form-fields->obj
  [fields]
  (into {}
        (mapv (fn [{:form-field/keys [value]
                    n                :form-field/name}]
                [(-> n name keyword) value])
              fields)))


(defn append-code-to-current-modal-editor [code-to-append]
  (let [code     @(re-posh/subscribe [:sql-editor/prop :sql-editor/code])
        new-code (str code "\n" code-to-append)]
    (dispatch [:sql-console-modal.events/set-editor-content new-code])))


(defn build-unit-test-code
  [test-key {:keys [table column period period-value]}]
  (let [sink-type @(re-posh/subscribe [:warehouse-data/prop :warehouse-data/sink-type])
        json-data @(re-posh/subscribe [:warehouse-data/prop :warehouse-data/json-data])
        gen-test  (get test-fn-map test-key)]
    (gen-test {:sink-type sink-type
               :catalog (:dut-db json-data)
               :schema (:dut-schema json-data)
               :table table
               :column column
               :period period
               :period-value period-value})))

(defn add-unit-test-to-sql-console
  [test-key values]
  (let [test-code (build-unit-test-code test-key values)]
    (append-code-to-current-modal-editor test-code)
    (dispatch [:app.events/add-snack :success "Added unit test"])))

(defn add-unit-test-to-sandbox-transform
  [transform-id test-key values]
  (let [test-code (build-unit-test-code test-key values)
        transform (app.utils/drop-ns-in-keys
                   @(re-posh/subscribe
                     [:sandbox-transform/pull
                      [:sandbox-transform/master-id transform-id]]))
        command   (or @(re-posh/subscribe
                        [:pipeline-transform-unsaved/prop
                         [:pipeline-transform-unsaved/id transform-id]
                         :pipeline-transform-unsaved/command])
                      (:command transform)
                      (:sql transform)
                      "")]
    (when-not @(re-posh/subscribe
                [:pipeline-transform-unsaved/pull
                 [:pipeline-transform-unsaved/id transform-id]])
      (dispatch [:pipeline-transform-unsaved/add
                 (->pipeline-transform-unsaved {:id transform-id
                                                :command command})]))
    (dispatch [:pipeline-editor.events/update-local-transform
               transform-id
               (str command "\n" test-code)])
    (dispatch [:app.events/add-snack :success "Added unit test"])
    (dispatch [:posthog/capture {:event (:sandbox/add-unit-test posthog-events/events)
                                 :props {:test-type (name test-key)}}])))

(defn split-table-name [table-name]
  (-> table-name (str/split ".") reverse))

(defn get-metadata-params [table-name]
  (let [[table schema catalog] (split-table-name table-name)

        snowflake? (= @(re-posh/subscribe [:warehouse-data/prop :warehouse-data/sink-type]) "snowflake")
        catalog    (or catalog
                       (when-not snowflake?
                         (-> @(re-posh/subscribe [:sql-metadata-partial/prop
                                                  [:sql-metadata-partial/params {:object "catalogs"}]
                                                  :sql-metadata-partial/metadata])
                             first
                             :table-cat)))]
    {:object "columns"
     :table   table
     :schema  schema
     :catalog catalog}))


(defn get-table-column-names
  [table-name & [filter-fn]]
  (let [params  (get-metadata-params table-name)
        columns (when table-name
                  @(re-posh/subscribe [:sql-metadata-partial/prop [:sql-metadata-partial/params params]
                                       :sql-metadata-partial/metadata]))]
    (cond->> columns
      (some? filter-fn)
      (filter filter-fn)

      true
      (mapv :column-name))))


(defn get-sandbox-table-column-names
  [table-name & [filter-fn]]
  (let [tables  @(re-posh/subscribe [:sandbox-table/pull-many])
        table   (some #(when (= (:sandbox-table/id %) table-name) %) tables)
        columns (:sandbox-table/columns table)]
    (cond->> columns
      (some? filter-fn)
      (filter filter-fn)

      true
      (mapv #(or (:column %) (:column_name %) (:column-name %))))))


(defn filter-non-existing-table
  [table-name]
  (let [params (get-metadata-params table-name)]
    (some-> @(re-posh/subscribe [:sql-metadata-partial/prop [:sql-metadata-partial/params params]
                                 :sql-metadata-partial/metadata])
            seq)))


(defn get-existing-tables
  [output-tables]
  (filterv filter-non-existing-table output-tables))


(defn unit-test-description
  [sandbox?]
  (if sandbox?
    "You will only be able to add unit tests to output tables available in the current sandbox."
    "You will only be able to add unit tests to output tables of this transform that currently exist in your data warehouse."))


(defn ->select-option [v]
  {:value v :text v})


(defn unit-test-section
  [{:keys                   [title description]
    {:keys [disabled form]} :button} children]
  [:section {:class "pb-5 flex flex-col gap-4 border-b last:border-b-0"}
   [:div {:class "flex flex-col gap-1"}
    [typography/h4 title]
    [:p {:class "text-kleene-text-secondary"}
     description]]

   [:div {:class "flex flex-col gap-3"}
    children

    [:div {:class "flex justify-end"}
     [button {:type     "submit"
              :data-cy  "unit-tests-add-unit-test-btn"
              :size     :sm
              :form     form
              :disabled disabled
              :icon     Plus}
      "Add unit test"]]]])


(defn null-section-render
  [{:keys [id fields on-submit tables disable get-columns]}]
  (let [{:keys [table column]} fields

        columns         (get-columns (:value table))
        tables-options  (mapv ->select-option tables)
        columns-options (mapv ->select-option columns)]

    [unit-test-section {:title       "Null in field"
                        :description "Checks none of the entries in a specified column have a NULL entry"
                        :button      {:form     id
                                      :disabled (or (some empty? (map #(-> % second :value) fields))
                                                    disable)}}
     [:form {:on-submit on-submit :id id}
      [:div {:class "flex flex-col gap-3"}
       [select {:name "table" :label "Table" :value (:value table) :on-change (:action table) :options tables-options}]
       [select {:name "column" :label "Column" :value (:value column) :on-change (:action column) :options columns-options}]]]]))


(defn null-section
  [{:keys [tables disable get-columns on-add]}]
  (let [id             :add-null-test-form
        on-delete      #(dispatch [:form-model/delete [:form-model/id id]])
        default-table  (first tables)
        default-column (when default-table (first (get-columns default-table)))
        default-fields [{:name :table :value default-table}
                        {:name :column :value default-column}]]
    [form-component/form
     (->form-model
      {:id        id
       :on-submit (fn [{:form-model/keys [fields]}]
                    (let [values (form-fields->obj fields)]
                      (on-add :null values)
                      (dispatch [:form-model/update [:form-model/id id] (->form-model {:id id :fields default-fields})])))
       :clean-up  on-delete
       :fields    default-fields})
     (fn [fields _ on-submit]
       (null-section-render {:id        id
                             :fields    fields
                             :on-submit on-submit
                             :tables    tables
                             :disable   disable
                             :get-columns get-columns}))]))


(defn uniqueness-section-render
  [{:keys [id fields on-submit tables disable get-columns]}]
  (let [{:keys [table column]} fields
        columns         (get-columns (:value table))
        tables-options  (mapv ->select-option tables)
        columns-options (mapv ->select-option columns)]
    [unit-test-section {:title       "Uniqueness"
                        :description "Checks the distinct number of entries in a specified field equals the table row count"
                        :button      {:form     id
                                      :disabled (or (some empty? (map #(-> % second :value) fields))
                                                    disable)}}
     [:form {:on-submit on-submit :id id}
      [:div {:class "flex flex-col gap-3"}
       [select {:name "table" :label "Table" :value (:value table) :on-change (:action table) :options tables-options}]
       [select {:name "column" :label "Column" :value (:value column) :on-change (:action column) :options columns-options}]]]]))


(defn uniqueness-section
  [{:keys [tables disable get-columns on-add]}]
  (let [id             :add-uniqueness-test-form
        on-delete      #(dispatch [:form-model/delete [:form-model/id id]])
        default-table  (first tables)
        default-column (when default-table (first (get-columns default-table)))
        default-fields [{:name :table :value default-table}
                        {:name :column :value default-column}]]
    [form-component/form
     (->form-model
      {:id        id
       :on-submit (fn [{:form-model/keys [fields]}]
                    (let [values (form-fields->obj fields)]
                      (on-add :uniqueness values)
                      (dispatch [:form-model/update [:form-model/id id] (->form-model {:id id :fields default-fields})])))
       :clean-up  on-delete
       :fields    default-fields})
     (fn [fields _ on-submit]
       (uniqueness-section-render {:id        id
                                   :fields    fields
                                   :on-submit on-submit
                                   :tables    tables
                                   :disable   disable
                                   :get-columns get-columns}))]))


(defn freshness-section-render
  [{:keys [id fields on-submit tables disable sink-type get-columns]}]
  (let [{:keys [table column period period-value]} fields
        columns         (get-columns (:value table) date-column?)
        tables-options  (mapv ->select-option tables)
        columns-options (mapv ->select-option columns)]

    [unit-test-section {:title       "Freshness"
                        :description "Checks the maximum entry of a specified DATETIME or TIMESTAMP field is within a specified time interval"
                        :button      {:form     id
                                      :disabled (or (some empty? (map #(-> % second :value) fields))
                                                    disable)}}
     [:form {:on-submit on-submit :id id}
      [:div {:class "flex flex-col gap-3"}
       [select {:name "table" :label "Table" :value (:value table) :on-change (:action table) :options tables-options}]
       [select {:name "column" :label "Column" :value (:value column) :on-change (:action column) :options columns-options}]
       [:div {:class "min-w-0"}
        [:label {:class "mb-1 block font-medium"}
         "Period"]

        [:div {:class "grid min-w-0 grid-cols-[96px_1fr]"}
         [input {:name        "period-value"
                 :value       (:value period-value)
                 :on-change   (:action period-value)
                 :type        :number
                 :class       "h-10 min-w-0"
                 :input-props {:class "h-10 rounded-r-none!"}}]

         [select {:name        "period"
                  :value       (:value period)
                  :on-change   (:action period)
                  :options     (if (= sink-type "bigquery") bq-periods periods)
                  :class       "h-10 min-w-0"
                  :field-props {:class "h-10 -ml-px rounded-l-none!"}}]]]]]]))


(defn freshness-section [{:keys [tables disable sink-type get-columns on-add]}]
  (let [id             :add-freshness-test-form
        on-delete      #(dispatch [:form-model/delete [:form-model/id id]])
        default-table  (first tables)
        default-column (when default-table (first (get-columns default-table date-column?)))
        default-fields [{:name :table :value default-table}
                        {:name :column :value default-column}
                        {:name :period :value (if (= sink-type "bigquery") "SECOND" "minutes")}
                        {:name :period-value :value "1" :process #(some-> % .-target .-value str)}]]
    [form-component/form
     (->form-model
      {:id        id
       :on-submit (fn [{:form-model/keys [fields]}]
                    (let [values (form-fields->obj fields)]
                      (on-add :freshness values)
                      (dispatch [:form-model/update [:form-model/id id] (->form-model {:id id :fields default-fields})])))
       :clean-up  on-delete
       :fields    default-fields})
     (fn [fields _ on-submit]
       (freshness-section-render {:id        id
                                  :fields    fields
                                  :on-submit on-submit
                                  :tables    tables
                                  :disable   disable
                                  :sink-type sink-type
                                  :get-columns get-columns}))]))


(defn missing-procedures-alert
  [{:keys [on-click on-check]}]
  [alert/alert {:title "Action required"
                :class "mb-4"}
   [:div {:class "flex flex-col gap-3"}
    [:p {:class "max-w-lg"}
     "Your warehouse is missing SQL procedures required to run unit tests. Please click the button below to create them. It is an one time action."]
    [:div {:class "flex gap-2"}
     [button {:variant  :warning
              :on-click on-click}
      "Create required procedures"]
     [button {:variant  :warning-border
              :on-click on-check}
      "Refresh"]]]])


(defn missing-output-tables-alert
  []
  [alert/alert {:title "Action required"}
   [:div {:class "flex flex-col gap-3"}
    [:p {:class "max-w-lg prose"}
     "The Output Table(s) of this transform do not exist in your warehouse. Please create the table(s) by either:"
     [:ul {:class "list-disc pl-6 leading-6 my-1"}
      [:li "running this transform"]
      [:li "creating the table(s) in the SQL console"]]]]])


(defn create-procedures-paragraph
  [{:keys [on-missing on-create-success]}]
  (check-procedures {:on-missing on-missing
                     :on-ready   on-create-success})

  (fn [{:keys [missing? on-create-success]}]
    (when missing?
      [missing-procedures-alert {:on-click #(create-procedures on-create-success)
                                 :on-check #(check-procedures {:on-missing on-missing
                                                               :on-ready   on-create-success})}])))


(defn fetch-unit-test-metadata
  [sandbox? output-tables]
  (doseq [table-name output-tables]
    (if sandbox?
      (dispatch [:sandbox.api/fetch-sandbox-table {:params {:id table-name}}])
      (dispatch [:sql-console.api/fetch-metadata (get-metadata-params table-name)]))))


(defn forms
  [{:keys [output-tables block? on-add sandbox?]}]
  (let [sink-type @(re-posh/subscribe [:warehouse-data/prop :warehouse-data/sink-type])
        loading?  (if sandbox?
                    @(re-posh/subscribe [:mold-http/loading? :sandbox.api/fetch-sandbox-table])
                    @(re-frame/subscribe [:mold-http/loading? :sql-console.api/fetch-metadata]))]
    (if loading?
      [:div {:class "my-2 flex gap-2 items-center"}
       [c.spinner/inline-configurable {:size 20}]
       "Loading database metadata..."]

      (let [get-columns (if sandbox?
                          get-sandbox-table-column-names
                          get-table-column-names)
            tables      (vec (remove empty?
                                     (if sandbox?
                                       output-tables
                                       (get-existing-tables output-tables))))]
        [:div {:class "flex flex-col gap-4"}
         (when (empty? tables)
           [missing-output-tables-alert])
         [null-section {:tables tables :disable block? :get-columns get-columns :on-add on-add}]
         [uniqueness-section {:tables tables :disable block? :get-columns get-columns :on-add on-add}]
         [freshness-section {:tables tables :disable block? :sink-type sink-type :get-columns get-columns :on-add on-add}]]))))


(defn sidebar
  [{:keys [transform-id output-tables]}]
  (let [[show-procedures-msg set-show-procedures-msg] (react/useState false)
        hide-button #(set-show-procedures-msg false)
        show-button #(set-show-procedures-msg true)]
    (react/useEffect
     (fn []
       (when (not-empty output-tables)
         (fetch-unit-test-metadata true output-tables))
       js/undefined)
     #js [transform-id (pr-str output-tables)])

    [:div {:class "p-6"}
     [:div {:class "mb-6"}
      [:p {:class "text-sm leading-5"}
       (unit-test-description true)]]

     (if (not-empty output-tables)
       [:<>
        [create-procedures-paragraph {:missing?          show-procedures-msg
                                      :on-missing        show-button
                                      :on-create-success hide-button}]
        [forms {:output-tables output-tables
                :sandbox?      true
                :block?        show-procedures-msg
                :on-add        #(add-unit-test-to-sandbox-transform transform-id %1 %2)}]]
       [:p {:class "text-sm"}
        "Select a sandbox transform with output tables to add unit tests."])]))


(defn modal []
  (let [[show-procedures-msg set-show-procedures-msg] (react/useState false)

        hide-button   #(set-show-procedures-msg false)
        show-button   #(set-show-procedures-msg true)
        opened?       @(re-posh/subscribe [:sql-console-modal.subs/unit-tests-modal-open?])
        transform-id         @(re-posh/subscribe [:sql-console-modal.subs/unit-tests-modal-transform])
        modal-output-tables  @(re-posh/subscribe [:sql-console-modal.subs/unit-tests-modal-output-tables])
        pipeline-editor? (= :pipeline-editor @(re-posh/subscribe [:app-route/prop :app-route/name]))
        sandbox?         (and pipeline-editor?
                              @(re-posh/subscribe [:sandbox.subs/enabled?]))
        transform            @(re-posh/subscribe [:transform/pull [:transform/id transform-id]])
        output-tables        (or modal-output-tables
                                 (:transform/output-tables transform))
        hide-modal-fn (fn hide-modal []
                        (dispatch [:sql-console-modal.events/toggle-unit-tests-modal false]))]

    (react/useEffect
     (fn []
       (when (and opened? (not sandbox?) (:transform/master-id transform))
         (dispatch [:transform.api/get-latest-version-by-master-id
                    {:master-id (:transform/master-id transform)}]))
       (fn []))
     #js [opened? transform-id sandbox?])

    (react/useEffect
     (fn []
       (when (and opened? (not-empty output-tables))
         (fetch-unit-test-metadata sandbox? output-tables))
       js/undefined)
     #js [opened? sandbox? (pr-str output-tables)])

    [dialog-root {:open           (boolean opened?)
                  :on-open-change #(when-not % (hide-modal-fn))}
     [dialog-content {:size :medium}
      [dialog-title "Add unit test"]
      [dialog-description
       (unit-test-description sandbox?)]

      (when (not-empty output-tables)
        [:div {:class "mt-4"}
         [create-procedures-paragraph {:missing?          show-procedures-msg
                                       :on-missing        show-button
                                       :on-create-success hide-button}]
         [forms {:output-tables output-tables
                 :sandbox?      sandbox?
                 :block?        show-procedures-msg
                 :on-add        add-unit-test-to-sql-console}]])]]))
